import { withoutUrlQuery } from './telemetryUtils';

/**
 * The parts of a Sentry event these helpers read. Structural, so the browser and Lambda SDKs can
 * each pass their own event type, whichever `@sentry/core` they bundle.
 */
type SentryEvent = {
	user?: { id?: string | number | undefined } | undefined;
	request?: { url?: string | undefined } | undefined;
	contexts?: { trace?: SentrySpan | undefined } | undefined;
};

/** The part of a span, or of a transaction's root span, these helpers read. */
type SentrySpan = { data?: Readonly<Record<string, unknown>> | undefined };

/** Keep Sentry's own `sentry.*` attributes and values that are not text, which can hold full URLs. */
const withoutTextAttributes = (attributes: Readonly<Record<string, unknown>>) =>
	Object.fromEntries(
		Object.entries(attributes).filter(
			([key, value]) => key.startsWith('sentry.') || typeof value !== 'string'
		)
	);

/**
 * Remove PII from a span, for a deployment that does not send it.
 *
 * @param span A finished span.
 * @returns The span without its text attributes, which can hold full URLs and queries.
 */
export const withoutSentrySpanPii = <Span extends SentrySpan>(span: Span): Span =>
	// `Object.assign` rather than a spread, so the result keeps the caller's own span type.
	Object.assign({}, span, span.data ? { data: withoutTextAttributes(span.data) } : {});

/**
 * Remove PII from a Sentry event, for a deployment that does not send it.
 *
 * Keeps the user's opaque ID, tags, contexts, the exception with its stack, and the request URL
 * without its query. Drops `extra` and breadcrumbs, which carry request bodies, socket messages
 * and console output.
 *
 * @param event An error or transaction event.
 * @returns A copy of the event without PII.
 */
export const withoutSentryPii = <Event extends SentryEvent>(event: Event): Event =>
	// `Object.assign` rather than a spread, so the result keeps the caller's own event type.
	Object.assign({}, event, {
		user: event.user?.id === undefined ? undefined : { id: event.user.id },
		request:
			event.request?.url === undefined
				? undefined
				: { url: withoutUrlQuery(event.request.url) },
		contexts: event.contexts?.trace
			? { ...event.contexts, trace: withoutSentrySpanPii(event.contexts.trace) }
			: event.contexts,
		extra: undefined,
		breadcrumbs: undefined,
	});
